import { AnimatePresence, motion } from "framer-motion";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import VideoCapture from "./VideoCaptureAndReview";
import { useState } from "react";
import { OnboardingStep, UserGender, UserProfile } from "@/generated-api";
import { ArrowLeft, HeartHandshake, InfoIcon } from "lucide-react";
import { Button } from "../ui/button";
import faceInstructions from "../../assets/face-scan-instructions.svg";
import figureInstructions from "../../assets/figure-scan-instructions.svg";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useTranslation } from "@/hooks/useTranslation";

enum FaceVerificationStep {
  INSTRUCTIONS = "instructions",
  VIDEO_CAPTURE = "video_capture",
}

export default function FaceVerification({ user }: { user: UserProfile }) {
  const [step, setStep] = useState(FaceVerificationStep.INSTRUCTIONS);
  const modalOpen = user.re_requested_step === OnboardingStep.FACE_VERIFICATION;
  const [requestModalOpen, setRequestModalOpen] = useState(modalOpen);
  const t = useTranslation();

  function MoreInfo() {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <InfoIcon className="w-6 h-6 stroke-[#9D9D9D] shrink-0" />
        </PopoverTrigger>
        <PopoverContent className="w-80 dark">
          <div className="text-sm text-[#9D9D9D]">
            {t.FACE_VERIFICATION.LABELS.MORE_INFO}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // function VerifyInSafari() {
  //   return (
  //     <div className="flex flex-row gap-3 items-center rounded-lg border border-blue-500/30 p-3 bg-blue-600/30 text-blue-500">
  //       <InfoIcon className="w-6 h-6 shrink-0" />
  //       <div className="text-sm">
  //         <div className="text-left">
  //           {t.FACE_VERIFICATION.LABELS.VERIFY_SAFARI}
  //         </div>
  //         <div className="flex justify-end mt-2">
  //           <CopyTextButton text="app.pulse-date.com" />
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <AnimatePresence mode="wait">
      {step == FaceVerificationStep.INSTRUCTIONS ? (
        <motion.div
          key="gender"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.1 }}
        >
          <Card className="dark min-w-[320px] w-full max-w-md mx-auto rounded-3xl border border-zinc-600">
            <CardHeader>
              <div className="flex flex-row items-center justify-between">
                <CardTitle>{t.FACE_VERIFICATION.LABELS.TITLE}</CardTitle>
                <MoreInfo />
              </div>
              <h2 className="mb-4 text-sm text-[#9D9D9D] max-w-md">
                {t.FACE_VERIFICATION.LABELS.DESCRIPTION(
                  user.gender === UserGender.FEMALE,
                )}
              </h2>
            </CardHeader>
            <CardContent>
              {user.gender == UserGender.FEMALE && (
                <h4 className="text-l font-semibold leading-none tracking-tight text-gray-200 mb-4">
                  {t.FACE_VERIFICATION.LABELS.STEP_ONE}
                </h4>
              )}
              <div className="flex justify-center mb-6">
                <img
                  src={faceInstructions}
                  alt="Face verification instructions"
                  className="w-1/2"
                />
              </div>
              {user.gender == UserGender.FEMALE && (
                <>
                  <h4 className="text-l font-semibold leading-none tracking-tight text-gray-200 mb-4">
                    {t.FACE_VERIFICATION.LABELS.STEP_TWO}
                  </h4>
                  <div className="flex justify-center">
                    <img
                      src={figureInstructions}
                      alt="Figure instructions"
                      className="w-1/2"
                    />
                  </div>
                </>
              )}
            </CardContent>
            <CardFooter className="flex flex-col">
              <Button
                className="w-full"
                onClick={() => setStep(FaceVerificationStep.VIDEO_CAPTURE)}
              >
                {t.SHARED.LABELS.CONTINUE_BUTTON}
              </Button>
            </CardFooter>
          </Card>
          <Dialog open={requestModalOpen} onOpenChange={setRequestModalOpen}>
            <DialogContent className="dark text-white">
              <DialogHeader>
                <DialogTitle className="flex flex-row items-center text-left mb-4">
                  <HeartHandshake className="h-5 w-5 mr-2" />
                  {t.FACE_VERIFICATION_ACTON_REQUIRED.TITLE}
                </DialogTitle>
                <DialogDescription className="text-left text-md">
                  {t.FACE_VERIFICATION_ACTON_REQUIRED.DESCRIPTION}
                </DialogDescription>
                <ul className="list-disc pl-6 space-y-1 pt-2 text-left text-md text-muted-foreground">
                  <li>{t.FACE_VERIFICATION_ACTON_REQUIRED.CLARITY}</li>
                  <li>{t.FACE_VERIFICATION_ACTON_REQUIRED.SAME_ON_PHOTOS}</li>
                </ul>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </motion.div>
      ) : (
        <motion.div
          key="form"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.1 }}
        >
          <Card className="dark min-w-[320px] w-full max-w-md mx-auto rounded-3xl border border-zinc-600">
            <CardHeader>
              <Button
                variant="ghost"
                className="w-20 px-0"
                onClick={() => setStep(FaceVerificationStep.INSTRUCTIONS)}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t.SHARED.LABELS.BACK_BUTTON}
              </Button>
              <div className="flex flex-row items-center justify-between">
                <CardTitle>{t.FACE_VERIFICATION.LABELS.TITLE}</CardTitle>
                <MoreInfo />
              </div>
              <h2 className="mb-4 text-sm text-[#9D9D9D]">
                {t.FACE_VERIFICATION.LABELS.PAGE_TWO_DESCRIPTION}
              </h2>
              {/* {isStandalone && isIOS && <VerifyInSafari />} */}
            </CardHeader>
            <CardContent className="p-1">
              <VideoCapture
                gender={user.gender}
                existingVideoUrl={
                  user.re_requested_step === OnboardingStep.FACE_VERIFICATION
                    ? null
                    : user.verification_url
                }
              />
            </CardContent>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
